import React, { useId, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { ALLERGY_SEVERITY_OPTIONS } from '../../../constants/patientLifecycle';
import type { Allergy, AllergySeverity } from '../../../dtos/patientmr/demographics.dto';

export interface AllergiesValue {
  allergies: Allergy[];
  allergiesNoneKnown: boolean;
}

export interface AllergiesSectionProps extends AllergiesValue {
  readOnly: boolean;
  /** Show the inline "required" error (after a save attempt) */
  showError: boolean;
  onChange: (value: AllergiesValue) => void;
}

/**
 * PAT-04: allergies are required — a list, or an explicit "No known allergies". Never both,
 * never blank. Returns an error message, or null when valid.
 */
export function validateAllergies(value: AllergiesValue): string | null {
  const hasList = value.allergies.some((a) => a.allergen.trim());
  if (hasList && value.allergiesNoneKnown) return 'Choose either an allergy list or "No known allergies", not both.';
  if (!hasList && !value.allergiesNoneKnown) return 'Record at least one allergy or tick "No known allergies".';
  return null;
}

const severityLabel = (s?: AllergySeverity) => ALLERGY_SEVERITY_OPTIONS.find((o) => o.value === s)?.label;

const AllergiesSection: React.FC<AllergiesSectionProps> = ({ allergies, allergiesNoneKnown, readOnly, showError, onChange }) => {
  const baseId = useId();
  const [allergen, setAllergen] = useState('');
  const [reaction, setReaction] = useState('');
  const [severity, setSeverity] = useState<AllergySeverity | ''>('');
  const [rowError, setRowError] = useState<string | null>(null);

  const error = showError ? validateAllergies({ allergies, allergiesNoneKnown }) : null;
  const hasList = allergies.length > 0;

  const addAllergy = () => {
    if (!allergen.trim()) {
      setRowError('Enter the allergen.');
      return;
    }
    setRowError(null);
    const row: Allergy = { allergen: allergen.trim() };
    if (reaction.trim()) row.reaction = reaction.trim();
    if (severity) row.severity = severity;
    onChange({ allergies: [...allergies, row], allergiesNoneKnown: false });
    setAllergen('');
    setReaction('');
    setSeverity('');
  };

  const removeAllergy = (index: number) =>
    onChange({ allergies: allergies.filter((_, i) => i !== index), allergiesNoneKnown });

  const id = (name: string) => `${baseId}-${name}`;
  const inputClass = 'mt-1 w-full rounded-md border border-slate-300 p-2 text-sm bg-white disabled:bg-gray-100 disabled:opacity-60';

  return (
    <div className="text-left" data-testid="allergies-section">
      <label className="flex items-center gap-2 text-sm text-slate-800">
        <input
          type="checkbox"
          checked={allergiesNoneKnown}
          disabled={readOnly || hasList}
          onChange={(e) => onChange({ allergies, allergiesNoneKnown: e.target.checked })}
        />
        No known allergies
      </label>
      {hasList && !readOnly && (
        <p className="mt-1 text-xs text-slate-500">Remove the listed allergies to mark "No known allergies".</p>
      )}

      {hasList && (
        <ul className="mt-3 divide-y divide-slate-200 rounded-md border border-slate-200" aria-label="Allergies">
          {allergies.map((a, index) => (
            <li key={`${a.allergen}-${index}`} className="flex items-center justify-between gap-2 p-2 text-sm">
              <span>
                <span className="font-medium text-slate-800">{a.allergen}</span>
                {a.reaction && <span className="text-slate-600"> — {a.reaction}</span>}
                {a.severity && (
                  <span
                    className={`ml-2 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                      a.severity === 'SEVERE'
                        ? 'bg-red-100 text-red-800'
                        : a.severity === 'MODERATE'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {severityLabel(a.severity)}
                  </span>
                )}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeAllergy(index)}
                  className="rounded p-1.5 text-red-600 hover:bg-red-50"
                  aria-label={`Remove allergy ${a.allergen}`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && !allergiesNoneKnown && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_auto] gap-2 items-end">
          <div>
            <label htmlFor={id('allergen')} className="block text-xs font-medium text-slate-700">Allergen</label>
            <input id={id('allergen')} value={allergen} onChange={(e) => setAllergen(e.target.value)} className={inputClass} placeholder="e.g. Penicillin" />
          </div>
          <div>
            <label htmlFor={id('reaction')} className="block text-xs font-medium text-slate-700">Reaction</label>
            <input id={id('reaction')} value={reaction} onChange={(e) => setReaction(e.target.value)} className={inputClass} placeholder="e.g. Hives" />
          </div>
          <div>
            <label htmlFor={id('severity')} className="block text-xs font-medium text-slate-700">Severity</label>
            <select id={id('severity')} value={severity} onChange={(e) => setSeverity(e.target.value as AllergySeverity | '')} className={inputClass}>
              <option value="">—</option>
              {ALLERGY_SEVERITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={addAllergy}
            className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
          >
            <Plus size={14} aria-hidden="true" /> Add allergy
          </button>
        </div>
      )}
      {rowError && <p className="mt-1 text-xs text-red-700">{rowError}</p>}
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
};

export default AllergiesSection;
